import type { ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  type CardProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

interface SortableCollapsibleCardProps {
  id: string;
  title: ReactNode;
  summary?: ReactNode;
  children: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  actions?: ReactNode;
  sortable?: boolean;
  variant?: CardProps['variant'];
  contentSx?: SxProps<Theme>;
}

export function SortableCollapsibleCard({
  id,
  title,
  summary,
  children,
  expanded,
  onToggle,
  actions,
  sortable = true,
  variant = 'outlined',
  contentSx,
}: SortableCollapsibleCardProps) {
  const { t: tc } = useTranslation('common');
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant={variant}
      sx={{
        overflow: 'hidden',
        opacity: isDragging ? 0.72 : 1,
        boxShadow: isDragging ? 6 : undefined,
        border: '1px solid',
        borderColor: (theme) =>
          expanded ? alpha(theme.palette.text.primary, 0.23) : theme.palette.divider,
        mb: expanded ? 2.5 : 0.5,
        transition: 'margin-bottom 0.2s ease, border-color 0.15s ease',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          bgcolor: expanded ? 'background.paper' : 'background.default',
          transition: 'background-color 0.15s ease',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {sortable && (
          <Tooltip title={tc('dragToReorder')}>
            <IconButton
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              size="small"
              edge="start"
              onClick={(event) => {
                event.stopPropagation();
              }}
              sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              aria-label={tc('dragToReorder')}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {!expanded && summary ? (
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ mt: 0.25 }}
            >
              {summary}
            </Typography>
          ) : null}
        </Box>

        {actions ? (
          <Box
            onClick={(event) => {
              event.stopPropagation();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {actions}
          </Box>
        ) : null}

        <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
            aria-label={expanded ? tc('collapse') : tc('expand')}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <CardContent sx={contentSx}>{children}</CardContent>
      </Collapse>
    </Card>
  );
}
